'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import FlipClock from '@/app/customer-homepage/components/FlipClock';
import Arrow from '@/app/customer-homepage/components/Arrow';
import WinnerPhoto from '@/app/customer-homepage/components/WinnerPhoto';
import { useLiveTicketsSold } from '@/lib/hooks/useLiveTicketsSold';
import { FACEBOOK_URL } from '@/lib/homepage/links';
import {
  fitDisplaySize,
  formatDraw,
  formatTime,
  price,
  sameLondonDay,
} from '@/lib/homepage/format';
import { bundleOffer } from '@/lib/homepage/bundles';
import { shortName } from '@/lib/names';
import { splitCashAlternative } from '@/lib/cashAlternative';
import { useOpened } from '@/lib/useOpened';

const CATEGORY_LABELS = {
  cash: 'Cash',
  experiences: 'Tickets & experiences',
  holidays: 'Holidays',
  products: 'Prizes',
};

// "Morag K., Falkirk": the winner's display name and town.
const winnerLine = (w) =>
  `${shortName(w.name || w.userName)}${w.location && w.location !== 'United Kingdom' ? `, ${w.location}` : ''}`;

/**
 * The top of a competition page: what it is, what a ticket costs and the
 * state of play (time left and tickets left while it's open; when it's drawn
 * once it closes; the winning ticket once it's drawn).
 */
export default function CompetitionHero({ competition: c, winners = [], enterHref = '#tickets' }) {
  const total = Number(c?.totalTickets || 0);
  const sold = useLiveTicketsSold(c?.id, c?.ticketsSold || 0, total);
  const left = Math.max(0, total - sold);
  const pct = total ? Math.min(100, Math.round((sold / total) * 100)) : 0;

  const closes = c?.endDate || c?.drawDate;
  const draws = c?.drawDate || c?.endDate;
  const closed = c?.status !== 'active' || (closes && new Date(closes).getTime() <= Date.now());
  // Published ahead of time: it counts down to opening instead.
  const opened = useOpened(c?.startDate);
  const comingSoon = !closed && !opened;
  const isDrawn = c?.status === 'drawn';
  const bundle = bundleOffer(c);
  const category = CATEGORY_LABELS[c?.category] || 'Prizes';
  const { title, cashLine } = splitCashAlternative(c);
  const winningTicket = c?.winningTicket ?? winners[0]?.winningNumber ?? null;
  const entriesHref = c?.slug
    ? `/competitions/${c.category || 'products'}/${c.slug}/entries`
    : null;

  const drawLine =
    closes && draws && sameLondonDay(closes, draws) && closes !== draws
      ? `Closes ${formatDraw(closes)}. Drawn live on Facebook at ${formatTime(draws)}.`
      : closes
        ? `Closes ${formatDraw(closes)}. Drawn live on Facebook.`
        : 'Drawn live on Facebook.';

  return (
    <section className="page-head" aria-labelledby="comp-title">
      <div className="max-w-[1520px] mx-auto px-[var(--gutter)] pt-[clamp(1.75rem,4vw,3.5rem)] pb-[clamp(2.75rem,5vw,4.25rem)]">
        <nav aria-label="Breadcrumb" className="text-sm font-semibold text-muted-foreground">
          <Link href="/all-competitions" className="hover:text-foreground">
            All competitions
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-accent-bright">{category}</span>
        </nav>

        <div className="mt-4 grid grid-cols-12 gap-x-6 gap-y-8 items-end">
          {/* A size container, so a long word in the title fits its column
              instead of running under the countdown. */}
          <div className="col-span-12 lg:col-span-7 [container-type:inline-size]">
            <h1
              id="comp-title"
              className="d -ml-[0.03em] max-w-[18ch]"
              style={{ fontSize: fitDisplaySize(title, 'clamp(2.25rem, 5.2vw, 5.25rem)', '100cqi') }}
            >
              {title}
            </h1>
            {cashLine && (
              <p className="mt-3 text-[clamp(1.125rem,2vw,1.75rem)] font-bold leading-tight text-accent-bright">
                {cashLine}
              </p>
            )}
            {c?.shortDescription && (
              <p className="mt-5 max-w-[46ch] text-[length:var(--t-lede)] leading-[1.45] text-foreground/85">
                {c.shortDescription}
              </p>
            )}
            <dl className="mt-7 flex flex-wrap items-end gap-x-10 gap-y-5">
              <div>
                <dt className="sr-only">Ticket price</dt>
                <dd className="flex items-baseline gap-2">
                  <span className="num text-[clamp(2.5rem,4.4vw,3.75rem)] text-accent-bright">
                    {price(c?.ticketPrice)}
                  </span>
                  <span className="font-semibold text-muted-foreground">a ticket</span>
                </dd>
              </div>
              {bundle && (
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    Bundle
                  </dt>
                  <dd className="mt-1 text-lg font-bold">
                    {bundle.qty} for {price(bundle.total)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="col-span-12 lg:col-start-8 lg:col-span-5">
            {comingSoon && (
              <div>
                <p className="text-sm font-semibold text-accent-bright mb-2">Coming soon · entries open in</p>
                <FlipClock
                  endDate={c.startDate}
                  className="[&_.flip]:text-[clamp(2rem,3.4vw,3.25rem)]"
                />
                <p className="mt-4 text-sm font-semibold text-foreground/85">
                  Opens {formatDraw(c.startDate)}. {drawLine}
                </p>
                {total > 0 && (
                  <p className="mt-2 text-sm text-muted-foreground tabular-nums">
                    {total.toLocaleString('en-GB')} tickets at {price(c?.ticketPrice)}
                  </p>
                )}
              </div>
            )}

            {!closed && !comingSoon && (
              <div>
                <p className="text-sm font-semibold text-muted-foreground mb-2">Entries close in</p>
                <FlipClock
                  endDate={closes}
                  className="[&_.flip]:text-[clamp(2rem,3.4vw,3.25rem)]"
                />
                <p className="mt-4 text-sm font-semibold text-foreground/85">{drawLine}</p>
                {total > 0 && (
                  <div className="mt-5">
                    <div className="h-2 bg-white/[0.12]" aria-hidden="true">
                      <div className="h-2 bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="mt-2 flex justify-between text-sm font-semibold tabular-nums">
                      <span>
                        {left.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')} left
                      </span>
                      <span className="text-muted-foreground">{pct}% sold</span>
                    </p>
                  </div>
                )}
                <a href={enterHref} className="btn-block btn-go mt-6">
                  Enter now <Arrow />
                </a>
              </div>
            )}

            {closed && !isDrawn && (
              <div>
                <p className="d text-[length:var(--t-h3)]">Entries closed.</p>
                <p className="mt-3 text-lg font-semibold text-foreground/85">
                  Drawn live on Facebook{draws ? `, ${formatDraw(draws)}` : ''}.
                </p>
                <p className="mt-1 text-sm text-muted-foreground tabular-nums">
                  {sold.toLocaleString('en-GB')} tickets sold
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                  <a
                    href={c?.liveStreamUrl || FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-block btn-go"
                  >
                    Watch live on Facebook <Arrow />
                  </a>
                  {entriesHref && (
                    <Link href={entriesHref} className="link-more">
                      Entry list <Arrow />
                    </Link>
                  )}
                </div>
              </div>
            )}

            {isDrawn && (
              <div>
                <p className="text-sm font-semibold text-muted-foreground">
                  Drawn {draws ? formatDraw(draws) : ''}
                </p>
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Winning ticket
                </p>
                {winningTicket != null ? (
                  <div className="mt-2 flex gap-1.5" aria-label={`Winning ticket ${winningTicket}`}>
                    {String(winningTicket)
                      .split('')
                      .map((d, i) => (
                        <span key={i} className="flip flip-l !min-w-[1.3em] text-accent-bright">
                          {d}
                        </span>
                      ))}
                  </div>
                ) : (
                  <p className="mt-2 font-semibold">To be confirmed</p>
                )}
                {winners.length > 0 && !winners.some((w) => w.photo) && (
                  <p className="mt-4 text-lg font-semibold">
                    {winners.map(winnerLine).join(' · ')}
                  </p>
                )}
                {/* Winners who agreed to a photo: square, beside their name. */}
                {winners.some((w) => w.photo) && (
                  <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-4">
                    {winners.map((w) => (
                      <li key={w.id} className="flex items-center gap-4 text-lg font-semibold">
                        {w.photo && (
                          <WinnerPhoto
                            src={w.photo}
                            alt={`Photo of ${shortName(w.name || w.userName)}`}
                            sizes="96px"
                            className="h-20 w-20 sm:h-24 sm:w-24"
                          />
                        )}
                        <span>{winnerLine(w)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
                  {c?.liveStreamUrl && (
                    <a
                      href={c.liveStreamUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-block btn-go"
                    >
                      Watch the draw <Arrow />
                    </a>
                  )}
                  {entriesHref && (
                    <Link href={entriesHref} className="link-more">
                      Entry list <Arrow />
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

CompetitionHero.propTypes = {
  competition: PropTypes.object.isRequired,
  winners: PropTypes.array,
  enterHref: PropTypes.string,
};
